import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import Button from "@khanacademy/wonder-blocks-button";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {ANCHOR_ICON_SIZE} from "@khan/guide-core";

import {useNotificationActionBubbleState} from "./state/use-notification-action-bubble-state.ts";

import caretUpIcon from "@phosphor-icons/core/regular/caret-up.svg";
import xIcon from "@phosphor-icons/core/regular/x.svg";

interface Props {
    /**
     * The label to display on the button.
     */
    label: string;
    /**
     * Optional style override.
     */
    style?: StyleType;
}

/**
 * A button that controls the visibility of the notification action bubbles.
 */
export const NotificationActionBubblesButton = (
    props: Props,
): React.ReactElement | null => {
    const {label, style} = props;
    const {
        state: {isCollapsed},
        handleDismiss,
        handleExpand,
    } = useNotificationActionBubbleState();
    return (
        <Button
            kind="secondary"
            aria-label={
                isCollapsed ? t`Show notifications` : t`Dismiss notifications`
            }
            endIcon={isCollapsed ? caretUpIcon : xIcon}
            style={[styles.notificationToggle, style]}
            onClick={isCollapsed ? handleExpand : handleDismiss}
        >
            {label}
        </Button>
    );
};

const styles = StyleSheet.create({
    notificationToggle: {
        flex: "0 0 auto",
        width: "fit-content",
        color: color.white,
        backgroundColor: color.eggplant,
        height: ANCHOR_ICON_SIZE,
        borderRadius: ANCHOR_ICON_SIZE / 2,
        paddingBlockStart: spacing.medium_16,
        paddingBlockEnd: spacing.medium_16,
        paddingInlineStart: spacing.medium_16,
        paddingInlineEnd: spacing.small_12,
        border: "none",
        // @ts-expect-error pseudo selectors aren't recognized as a CSSProperty
        ":hover:not(:disabled)": {
            color: color.white,
            backgroundColor: color.eggplant,
            outlineOffset: "2px",
        },
        ":hover:not(:disabled) div": {
            color: color.white,
        },
    },
});
